import { ListItemText, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { StatusIcon } from '@/components/DataDisplay/StatusIcon';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import {
  DocumentStatus,
  DocumentType,
  RequiredDocument,
} from '@/types/document';
import { User } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { isCaymanUser } from '@/utils/user';

import { RequiredDocumentItemContainer } from './RequiredDocuments.styled';

type RequiredDocumentItemProps = {
  doc: RequiredDocument;
  isInactive?: boolean;
  onClick: (doc: RequiredDocument) => void;
};

export function RequiredDocumentItem({
  doc,
  isInactive,
  onClick,
}: Readonly<RequiredDocumentItemProps>) {
  const theme = useTheme();
  const user = useAppSelector(selectLoggedInUser);
  const { t } = useTranslate();

  const { subtitle, subtitleColorFn } = useMemo(() => {
    let content = doc.subtitle;

    switch (doc.status) {
      case DocumentStatus.InReview:
        content = 'status.underReview';
        break;
      case DocumentStatus.Rejected:
        content = 'status.rejected';
        break;
      case DocumentStatus.Approved:
        content = 'status.approved';
        break;
    }

    return {
      subtitle: content,
      subtitleColorFn:
        theme.palette.status[doc.status || DocumentStatus.Pending]?.text ||
        theme.palette.text.secondary,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.status, doc.subtitle]);

  const { handleClick, isFaded } = useMemo(() => {
    const isUploaded = [
      DocumentStatus.Approved,
      DocumentStatus.InReview,
    ].includes(doc.status as DocumentStatus);

    return {
      handleClick: isInactive && !isUploaded ? undefined : () => onClick(doc),
      isFaded: isInactive && !isUploaded,
    };
  }, [doc, isInactive, onClick]);

  return (
    <RequiredDocumentItemContainer
      {...getDataTestId(`required-document-item-${doc.name}`)}
      onClick={handleClick}
      isFaded={isFaded}
    >
      {doc.status !== DocumentStatus.Pending ? (
        <StatusIcon value={doc.status as DocumentStatus} size={40} />
      ) : (
        <StatusIcon
          value=""
          iconName={doc.icon}
          backgroundColor={theme => theme.palette.primaries[100]}
          color={theme => theme.palette.primaries[400]}
          size={40}
        />
      )}
      <ListItemText>
        <Typography variant="body2">
          {doc.displayHTML ? (
            <span
              dangerouslySetInnerHTML={{
                __html: t(doc.displayHTML) || '',
              }}
            />
          ) : (
            getDocumentName(t, doc.name, user)
          )}
        </Typography>
        <Typography variant="caption" color={subtitleColorFn}>
          {t(subtitle || '')}
        </Typography>
      </ListItemText>
    </RequiredDocumentItemContainer>
  );
}

function getDocumentName(
  t: (key: string) => string,
  docName: string,
  user?: User,
) {
  if (isCaymanUser(user)) {
    if (docName !== DocumentType.Source_Of_Fund_From_Other) {
      return t(`documentType.${docName}`);
    }
    const otherIncomeDescription = user?.incomeSources?.find(
      source => source.sourceType === 'OTHER',
    )?.description;

    return (
      t(`documentType.${docName}`) +
      (otherIncomeDescription ? ` - ${otherIncomeDescription}` : '')
    );
  }
  return t(`documentType.${docName}`);
}
